import { getCoreUtils } from "./kCoreHandler";

/**
 * kBytesCore — turning a typed-array VIEW into the ArrayBuffer that holds exactly its bytes.
 *
 * WHY THIS EXISTS. A `Uint8Array` is a WINDOW onto an ArrayBuffer, not the buffer. `.buffer` hands
 * back the WHOLE thing — every byte before `byteOffset` and every byte after `byteOffset +
 * byteLength` — and three very ordinary producers return windows:
 *
 *   * `Uint8Array.prototype.subarray`, which over-allocating decoders use to trim their result.
 *     `kMimeBodyCore.Base64ToBytes` does exactly that.
 *   * a Node / browserify `Buffer`, which for small allocations is a view into a SHARED POOL at an
 *     arbitrary offset — so `.buffer` there can expose bytes belonging to somebody else's
 *     allocation entirely.
 *   * `DataView`-driven parsers that hand out slices of a larger file.
 *
 * ⚠ IT SHIPPED. On 2026-08-18 `kFileDownload.SaveBytes` built its Blob from `.buffer` and saved
 * every wrapped-base64 mail attachment with trailing padding — measured at 22 real bytes against 29
 * reaching the Blob. Silent, because the file is LONGER rather than absent: a .txt picks up
 * junk and a zip-shaped file gets bytes after its EOF record.
 *
 * ⚠ THE ESTATE ALREADY KNEW. `kGeoPlaceIndexCore` copies into "a private, offset-zero buffer" and
 * says why; `kExifReaderCore` and `kSPUserPhotoCore` both pass `byteOffset`/`byteLength` through
 * correctly. What was missing was ONE place to ask, so the PDF cluster could miss it while three
 * other files got it right.
 */
export class kBytesCore {

    /**
     * The ArrayBuffer holding EXACTLY these bytes — the view's own buffer when that is already true,
     * and a copy when it is not.
     *
     * ⚠ IT COPIES ONLY WHEN IT MUST, which is what makes it safe to use everywhere. The common case
     * (an exactly-sized array) returns the same buffer and allocates nothing, so replacing a bare
     * `.buffer` with this call costs a 2-integer comparison — there is no reason for a call site to
     * "optimise" it back out, and doing so is how the defect above returns.
     *
     * ⚠ THE CAST IS HERE AND NOWHERE ELSE. `.buffer` is typed `ArrayBufferLike` because it may be a
     * SharedArrayBuffer; every consumer that wanted a concrete `ArrayBuffer` used to carry its own
     * `as ArrayBuffer`, which is six chances to also drop the windowing question. One cast, one
     * place, one explanation.
     */
    public static ExactBuffer = (Bytes: Uint8Array): ArrayBuffer => {
        if (getCoreUtils().KErrorMode === true) { return new ArrayBuffer(0); }
        try {
            if (Bytes.byteOffset === 0 && Bytes.byteLength === Bytes.buffer.byteLength) {
                return Bytes.buffer as ArrayBuffer;
            }
            const Out: ArrayBuffer = new ArrayBuffer(Bytes.byteLength);
            new Uint8Array(Out).set(Bytes);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBytesCore.ExactBuffer");
            return new ArrayBuffer(0);
        }
    };

    /**
     * True when a view already owns its whole buffer.
     *
     * Exposed because "is this a window?" is the question a TEST needs to ask to prove a fixture is
     * the shape it claims to be — a windowing test built on an exactly-sized fixture passes under
     * both the correct and the broken implementation, which is how this class's own first test
     * nearly measured nothing.
     */
    public static IsExact = (Bytes: Uint8Array): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return Bytes.byteOffset === 0 && Bytes.byteLength === Bytes.buffer.byteLength;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBytesCore.IsExact");
            return false;
        }
    };

    /**
     * Decode ONE base64url segment (a JWT header/payload/signature) into its bytes.
     *
     * ⚠ base64url IS NOT base64, and the difference is not cosmetic. It swaps `+`→`-` and `/`→`_` so
     * the value survives a URL, and it drops the `=` padding. Handing such a segment to an ordinary
     * base64 decoder yields WRONG BYTES for any input containing either character, rather than an
     * error — which on a token-verification path means a signature that silently fails to match, or
     * worse, claims parsed from mis-decoded bytes.
     *
     * ⚠ IT LIVES HERE BECAUSE NOTHING SHARED COULD DECODE IT. `kCertTokenMinter` only ENCODES, and
     * `kMimeBodyCore.Base64ToBytes` handles the standard alphabet. The estate therefore had two
     * private copies of this in one folder — `MoseOperatorGuard` and `MoseTokenVerifier` — both
     * parsing attacker-reachable JWT segments, which is the last place a codebase should be keeping
     * two answers to one question.
     *
     * Returns EMPTY for an unusable segment. That is the safe direction here: a caller comparing a
     * signature or reading claims must fail to match on empty, never proceed on a guess.
     */
    public static B64UrlBytes = (Segment: string): Uint8Array => {
        if (getCoreUtils().KErrorMode === true) { return new Uint8Array(0); }
        try {
            if (typeof Segment !== "string" || Segment === "") { return new Uint8Array(0); }
            let Standard: string = Segment.split("-").join("+").split("_").join("/");
            while ((Standard.length % 4) !== 0) { Standard = Standard + "="; }
            const G: Record<string, unknown> = globalThis as unknown as Record<string, unknown>;
            const Buf: unknown = G.Buffer;
            if (Buf !== undefined && Buf !== null) {
                const B: { from: (s: string, enc: string) => Uint8Array } =
                    Buf as { from: (s: string, enc: string) => Uint8Array };
                const NodeBytes: Uint8Array = B.from(Standard, "base64");
                return new Uint8Array(NodeBytes.buffer, NodeBytes.byteOffset, NodeBytes.byteLength);
            }
            const AtobFn: unknown = G.atob;
            if (typeof AtobFn === "function") {
                const Bin: string = (AtobFn as (s: string) => string)(Standard);
                const Bytes: Uint8Array = new Uint8Array(Bin.length);
                for (let I: number = 0; I < Bin.length; I = I + 1) {
                    Bytes[I] = Bin.charCodeAt(I);
                }
                return Bytes;
            }
            return new Uint8Array(0);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBytesCore.B64UrlBytes");
            return new Uint8Array(0);
        }
    };

    /**
     * Standard base64 (with `=` padding) of raw bytes — the body of a `data:` URI, a font for pdfMake,
     * an image for an offline render. "" for no bytes, or when the runtime has neither encoder.
     *
     * Prefers Node's `Buffer` (Azure Functions); falls back to CHUNKED `btoa` in a browser, because
     * `String.fromCharCode.apply` over a whole buffer overflows the call stack at roughly 100 KB — a
     * font or a photo, i.e. exactly the inputs this is for. Honours `byteOffset`/`byteLength`, so a
     * window onto a larger buffer encodes only its own bytes (see the class comment).
     *
     * Lifted 2026-09-26 from the private `kSPUserPhotoCoreTask._ToBase64`, which now delegates here;
     * `kDocPdfBytes.ToBase64` in ReUsableTS was a second copy of the same technique.
     */
    // ERROR-SUPPRESSED-JUSTIFIED: default options preserve optional-host fallback; required callers
    // retain their original report owner through ErrorLocation instead of a shared diagnostic label.
    public static ToBase64 = (Bytes: Uint8Array, RequireEncoder: boolean = false,
        ErrorLocation: string = "kBytesCore.ToBase64"): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Bytes === null || Bytes === undefined || Bytes.byteLength === 0) { return ""; }
            const G: Record<string, unknown> = globalThis as unknown as Record<string, unknown>;
            const Buf: unknown = G.Buffer;
            if (Buf !== undefined && Buf !== null) {
                const B: { from: (b: ArrayBufferLike, Offset: number, Length: number) => { toString: (enc: string) => string } } =
                    Buf as { from: (b: ArrayBufferLike, Offset: number, Length: number) => { toString: (enc: string) => string } };
                return B.from(Bytes.buffer, Bytes.byteOffset, Bytes.byteLength).toString("base64");
            }
            const Btoa: unknown = G.btoa;
            if (typeof Btoa !== "function") {
                if (RequireEncoder === true) { throw new Error("Byte base64 encoding is unavailable on this host."); }
                // ERROR-SUPPRESSED-JUSTIFIED: an optional unsupported host retains the defined empty answer.
                return "";
            }
            return (Btoa as (s: string) => string)(kBytesCore._BinaryString(Bytes));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, ErrorLocation);
            return "";
        }
    };

    /** UTF-8 text encoded on Node or a browser. Optional hosts without an encoder return "";
     * required callers report that absence. ErrorLocation retains the caller's reporting owner. */
    public static ToBase64Utf8 = (Text: string, RequireEncoder: boolean = false,
        ErrorLocation: string = "kBytesCore.ToBase64Utf8"): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Host: Record<string, unknown> = globalThis as unknown as Record<string, unknown>;
            const BufferSource: unknown = Host.Buffer;
            if (BufferSource !== undefined) {
                const Encoder: { from: (Value: string, Encoding: string) => { toString: (Encoding: string) => string } } =
                    BufferSource as { from: (Value: string, Encoding: string) => { toString: (Encoding: string) => string } };
                return Encoder.from(Text, "utf8").toString("base64");
            }
            const BrowserEncoder: unknown = Host.btoa;
            if (typeof BrowserEncoder === "function") {
                const Bytes: string = encodeURIComponent(Text).replace(/%([0-9A-F]{2})/g, kBytesCore._Utf8Byte);
                return (BrowserEncoder as (Value: string) => string)(Bytes);
            }
            if (RequireEncoder === true) { throw new Error("UTF-8 base64 encoding is unavailable on this host."); }
            // ERROR-SUPPRESSED-JUSTIFIED: optional unsupported hosts must return no token without latching the gate.
            return "";
        } catch (e: unknown) {
            getCoreUtils().KGlobalErrorHandler(e, ErrorLocation);
            return "";
        }
    };

    /** URL-safe UTF-8 base64 without padding; the Graph-specific "u!" prefix belongs to its caller.
     * ERROR-SUPPRESSED-JUSTIFIED: errors are reported through the caller-supplied ErrorLocation;
     * preserving the owning Graph/MIME diagnostic label requires a dynamic report label. */
    public static ToBase64UrlUtf8 = (Text: string, RequireEncoder: boolean = false,
        ErrorLocation: string = "kBytesCore.ToBase64UrlUtf8"): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Raw: string = kBytesCore.ToBase64Utf8(Text, RequireEncoder, ErrorLocation);
            return Raw.replace(/=+$/, "").replace(/\//g, "_").replace(/\+/g, "-");
        } catch (e: unknown) {
            getCoreUtils().KGlobalErrorHandler(e, ErrorLocation);
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: deterministic percent-byte replacer; its caller owns thrown errors. */
    private static _Utf8Byte = (_Whole: string, Hex: string): string => {
        return String.fromCharCode(parseInt(Hex, 16));
    };

    /** The bytes as a "binary string" (one char per byte) for btoa, built in 8 KB chunks. */
    private static _BinaryString = (Bytes: Uint8Array): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Bin: string = "";
            const Chunk: number = 8192;
            for (let I: number = 0; I < Bytes.length; I = I + Chunk) {
                const Slice: Uint8Array = Bytes.subarray(I, Math.min(I + Chunk, Bytes.length));
                Bin = Bin + String.fromCharCode.apply(null, Array.prototype.slice.call(Slice) as number[]);
            }
            return Bin;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBytesCore._BinaryString");
            return "";
        }
    };

    /** The same segment decoded as UTF-8 text — the JWT header/payload form. "" when unusable. */
    public static B64UrlText = (Segment: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Bytes: Uint8Array = kBytesCore.B64UrlBytes(Segment);
            if (Bytes.byteLength === 0) { return ""; }
            return new TextDecoder("utf-8").decode(Bytes);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBytesCore.B64UrlText");
            return "";
        }
    };
}
